import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  LayoutDashboard,
  CheckSquare,
  Package,
  Map,
  FileText,
  AlertCircle,
  Loader2,
  Receipt
} from 'lucide-react';
import { ProjectDetailsStepper } from '../components/ProjectDetailsStepper';
import { ProjectOverviewTab } from '../components/ProjectOverviewTab';
import { ProjectInventoryTab } from '../components/ProjectInventoryTab';
import { ProjectSiteInventoryTab } from '../components/ProjectSiteInventoryTab';
import { ProjectTasksTab } from '../components/ProjectTasksTab';
import { ProjectDocumentsTab } from '../components/ProjectDocumentsTab';
import { ProjectSideStats } from '../components/ProjectSideStats';
import { BASE_URL, downloadFile } from '@/lib/API';
import { projectService } from '../services/projectService';
import { PROJECT_PIPELINE_STAGES } from '../constants/projectConstants';
import { getActivePipelineIndex } from '../constants/projectPipelineStages';
import Toast from '@/components/ui/Toast';

export const ProjectDetailsScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Role-based access control
  const userStr = localStorage.getItem('blum_user') || localStorage.getItem('venus_user') || localStorage.getItem('user') || '{}';
  const user = JSON.parse(userStr);
  const role = (user.role || 'installation_team').toLowerCase();
  const isManagement = ['owner', 'admin', 'designer'].includes(role);

  const [activeTab, setActiveTab] = useState(isManagement ? 'Overview' : 'Tasks');
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchProject = React.useCallback(async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      const data = await projectService.getProjectById(id);
      setProject(data);
      setError(null);
    } catch (err) {
      // User requested: "as of now i want frontend only so carefully comment if any backend is stopping"
      // setError(err.message);
      console.warn("Backend failed to load project. Using mock project for frontend display.", err);
      setProject({
        _id: id,
        projectId: id,
        customerName: 'Mock Customer',
        status: 'design',
        progress: 10,
        installationMilestones: {},
        projectManager: null,
      });
      setError(null);
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [id]);

  const handleDownloadInvoice = async () => {
    setIsDownloading(true);
    try {
      const url = `${BASE_URL}/api/verve-projects/${project._id}/invoice`;
      await downloadFile(url, `Invoice-${project.projectId}.pdf`);
    } catch (err) {
      console.error("Download error:", err);
      setToast({ message: err.message || 'Failed to download invoice', type: 'error' });
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const tasksInventoryLocked = Boolean(
    project?.documentsStageComplete || project?.balanceDocumentsUploaded
  );

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
        <Loader2 className="w-8 h-8 text-[#2b448f] animate-spin" />
        <span className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest">Loading Project Pulse...</span>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <AlertCircle className="w-12 h-12 text-rose-500" />
        <div className="text-center">
          <h3 className="text-lg font-extrabold text-slate-800">Project Not Found</h3>
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-tighter">{error || 'Unable to retrieve project details.'}</p>
        </div>
        <button
          onClick={() => navigate('/projects')}
          className="px-6 py-2 bg-[#2b448f] text-white rounded-lg text-[11px] font-extrabold uppercase tracking-wider"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  const tradesComplete =
    project.installationMilestones?.civil &&
    project.installationMilestones?.electrical &&
    project.installationMilestones?.mechanical;
  const statusLc = String(project.status || '').toLowerCase();

  const showDocumentsTab =
    isManagement &&
    tradesComplete &&
    (['balance update', 'documents', 'completed'].includes(statusLc) ||
      project.balanceDocumentsUploaded);

  const baseTabs = [
    { id: 'Overview', icon: LayoutDashboard, label: 'Overview' },
    { id: 'Tasks', icon: CheckSquare, label: 'Tasks' },
    { id: 'Inventory Request', icon: Package, label: 'Inventory Request' },
    { id: 'Site Inventory', icon: Map, label: 'Site Inventory' },
    ...(showDocumentsTab ? [{ id: 'Documents', icon: FileText, label: 'Documents' }] : [])
  ];

  // Overview tab enabled only for Management
  const tabs = isManagement ? baseTabs : baseTabs.filter((t) => t.id !== 'Overview');

  return (
    <div className="flex flex-col gap-6 w-full animate-in fade-in duration-700 pb-10">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {/* Premium Header Container */}
      <div className="flex flex-col gap-5">
        {/* Breadcrumb & Navigation */}
        <div className="flex items-center gap-4 relative">
          <button
            onClick={() => navigate('/projects')}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-slate-100 text-slate-400 hover:text-[#2b448f] transition-all shrink-0"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex-1 flex items-center justify-between min-w-0">
            {/* Left: ID & Customer */}
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2.5">
                <span className="text-[13px] font-black text-blue-400 uppercase">
                  {project.leadDetails?.customId || project.customId || project.projectId || "—"}
                </span>
                <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[9px] font-extrabold capitalize whitespace-nowrap">
                  {(() => {
                    const activeIdx = getActivePipelineIndex(project.projectStatus || project.status, project.installationMilestones || {}, {
                      documentsStageComplete: !!project.documentsStageComplete,
                      teamAssigned: (project.fieldOfficers?.length || 0) > 0,
                      pmAssigned: !!project.projectManager
                    });
                    return PROJECT_PIPELINE_STAGES[activeIdx] || project.status;
                  })()}
                </span>
                {(project.completionReminderCount ?? 0) > 0 && (
                  <span className="text-[9px] font-bold text-red-600 tracking-tight whitespace-nowrap animate-pulse">
                    {(project.completionReminderCount ?? 0) > 1
                      ? `Reminders (${project.completionReminderCount})`
                      : 'Reminder'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5 min-w-0">
                <span className="text-[13px] font-semibold text-slate-700  tracking-tight">
                  {project.customerName}
                </span>
                {project.projectName && (
                  <span className="text-[13px] font-semibold text-slate-700 truncate tracking-tighter shrink-0 bg-slate-50 px-1.5 rounded">
                    {project.projectName}
                  </span>
                )}
              </div>
            </div>

            {/* Middle: Invoice Button */}
            {isManagement && project && (getActivePipelineIndex(project.projectStatus || project.status, project.installationMilestones || {}, { documentsStageComplete: !!project.documentsStageComplete, teamAssigned: (project.fieldOfficers?.length || 0) > 0, pmAssigned: !!project.projectManager }) >= 11) && (
              <button
                onClick={handleDownloadInvoice}
                disabled={isDownloading}
                title="Download Project Invoice"
                className={`w-11 h-11 flex items-center justify-center text-white rounded-2xl shadow-sm transition-all shrink-0 ${isDownloading ? 'bg-slate-300' : 'bg-emerald-600 hover:bg-emerald-700 hover:scale-105 active:scale-95'
                  }`}
              >
                {isDownloading ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
              </button>
            )}

            {/* Right: Progress Slider (Desktop) */}
            <div className="hidden md:flex items-center gap-4 shrink-0">
              <div className="flex items-center gap-3 px-4 py-1.5">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={project.progress || 0}
                  readOnly
                  style={{
                    background: `linear-gradient(to right, #2b448f ${(project.progress || 0)}%, #e2e8f0 ${(project.progress || 0)}%)`
                  }}
                  className="w-[520px] h-1.5 rounded-full appearance-none accent-[#2b448f] cursor-default"
                />
                <span className="text-[13px] font-black text-[#2b448f] tabular-nums">
                  {project.progress || 0}%
                </span>
              </div>
            </div>

            {/* Mobile Progress */}
            <div className="md:hidden flex items-center gap-2">
              <span className="text-[11px] font-black text-[#2b448f]">{project.progress || 0}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="w-full flex flex-col gap-6">
        <div className="flex flex-col gap-8 animate-in fade-in duration-500">
          <ProjectDetailsStepper
            currentStatus={project.status}
            milestones={project.installationMilestones}
            documentsStageComplete={project.documentsStageComplete}
            teamAssigned={(project.fieldOfficers?.length || 0) > 0}
            pmAssigned={!!project.projectManager}
          />

          <div className="flex flex-col lg:flex-row gap-6 items-start w-full">
            {/* Left Column - Consolidated Project Overview */}
            <div className="w-full lg:flex-1 flex flex-col gap-6 min-w-0">
              <ProjectOverviewTab
                project={project}
                onUpdate={fetchProject}
                setActiveTab={() => { }}
                tasksInventoryLocked={tasksInventoryLocked}
              />
            </div>

            {/* Right Column - Compact Project Stats & Activity */}
            <div className="w-full lg:w-[320px] xl:w-[360px] shrink-0 flex flex-col gap-6">
              <ProjectSideStats
                project={project}
                formatCurrency={(val) => Number(val || 0).toLocaleString('en-IN')}
                onProjectUpdated={fetchProject}
                viewerRole={role}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
